import axios from "axios";
import { createEmptyLogsAndVars, recordPotentialAxiosError, recordVariable, ReportingError, } from "../diagnostics/clientErrors";
import { baseUrl, encodeToBase58 } from "./dwcommonutils";
import { decryptData, getSymmetricKey } from "../dataEncryption";
import { refreshAuthToken } from "./refreshToken";
const MAX_CHUNK_SIZE = process.env.MAX_CHUNK_SIZE ? parseInt(process.env.MAX_CHUNK_SIZE) : 67_108_836;
const MAX_CHUNK_SIZE_GROSS = MAX_CHUNK_SIZE + 12 + 16;
export async function jwtUploadFileAndMessageReturnsJson(method, endpoint, authToken, sendObject, file, onProgress) {
    const url = endPointToUrl(endpoint);
    const message = encodeSendObjectToBase58(sendObject);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("message", message);
    return await commRequest(method, url, authToken, undefined, formData, undefined, undefined, onProgress);
}
export async function jwtUploadFileReturnsJson(method, endpoint, authToken, file, signal) {
    const url = endPointToUrl(endpoint);
    const formData = new FormData();
    formData.append("file", file);
    return await commRequest(method, url, authToken, undefined, formData, undefined, undefined, undefined, undefined, false, signal);
}
export async function jwtPlainGetParamReturnsJson(method, endpoint, authToken, params) {
    const url = endPointToUrl(endpoint);
    return await commRequest(method, url, authToken, { ...params });
}
export async function jwtNoParamReturnsJson(method, endpoint, authToken, signal) {
    const url = endPointToUrl(endpoint);
    return await commRequest(method, url, authToken, undefined, undefined, undefined, undefined, undefined, undefined, false, signal);
}
export async function jwtNoParamReturnsBinary(method, endpoint, authToken) {
    const url = endPointToUrl(endpoint);
    //const url = "https://httpbin.org/get";
    return await commRequest(method, url, authToken, undefined, undefined, "blob");
}
export async function jwtMessageAsGetReturnsJson(method, endpoint, authToken, sendObject, signal) {
    const url = endPointToUrl(endpoint);
    const message = encodeSendObjectToBase58(sendObject);
    return await commRequest(method, url, authToken, { message }, undefined, undefined, undefined, undefined, undefined, false, signal);
}
export async function jwtMessageAsGetReturnsBinary(method, endpoint, authToken, sendObject, onProgress, signal) {
    const url = endPointToUrl(endpoint);
    const message = encodeSendObjectToBase58(sendObject);
    return await commRequest(method, url, authToken, { message }, undefined, "blob", undefined, undefined, onProgress, false, signal);
}
export async function jwtMessageAsGetReturnsStream(method, endpoint, authToken, sendObject, onProgress) {
    const url = endPointToUrl(endpoint);
    const message = encodeSendObjectToBase58(sendObject);
    return await commRequest(method, url, authToken, { message }, undefined, "stream", undefined, undefined, onProgress);
}
export async function jwtChunkedMessageAsGetReturnsBinary(method, endpoint, authToken, sendObject, start, end, onProgress) {
    const url = endPointToUrl(endpoint);
    const message = encodeSendObjectToBase58(sendObject);
    return await commRequest(method, url, authToken, { message }, undefined, "stream", `bytes=${start}-${end}`, undefined, onProgress);
}
export const myReadFileInChunksAsReadableStream = async (provider, endPointUrl, authToken, path, size, progressData, calculateEstimated, onProgress, signal) => {
    const startTime = Date.now();
    const totalChunks = Math.ceil(size / MAX_CHUNK_SIZE);
    const sizeWithOverhead = size + totalChunks * 28; // IV: 12 bytes, authentication tag: 16 bytes
    const sendObject = { path: path };
    const message = encodeSendObjectToBase58(sendObject);
    const params = new URLSearchParams();
    params.append("message", message);
    const symmetricKey = await getSymmetricKey(provider);
    const downloadController = new AbortController();
    let canceledByConsumer = false;
    const abortDownload = () => downloadController.abort(signal?.reason);
    if (signal?.aborted) {
        abortDownload();
    }
    else {
        signal?.addEventListener("abort", abortDownload, { once: true });
    }
    const readableStream = new ReadableStream({
        async start(controller) {
            const startTimestamp = Date.now();
            let receivedLength = 0; // received that many bytes at the moment
            const url = endPointToUrl(endPointUrl);
            let currentToken = authToken;
            for (let i = 0; i < totalChunks; i++) {
                const start = i * MAX_CHUNK_SIZE_GROSS;
                const end = Math.min(start + MAX_CHUNK_SIZE_GROSS, sizeWithOverhead) - 1; // Ensure end is inclusive
                let response = await fetch(`${url}?${params}`, {
                    method: "GET",
                    signal: downloadController.signal,
                    headers: {
                        Authorization: "Bearer " + currentToken,
                        Range: `bytes=${start}-${end}`,
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                    },
                    cache: 'no-store',
                });
                if (response.status === 401) {
                    const newToken = await refreshAuthToken();
                    if (downloadController.signal.aborted) {
                        if (canceledByConsumer)
                            return;
                        throw downloadController.signal.reason ?? Object.assign(new Error("canceled"), { name: "AbortError" });
                    }
                    if (newToken) {
                        currentToken = newToken;
                        response = await fetch(`${url}?${params}`, {
                            method: "GET",
                            signal: downloadController.signal,
                            headers: {
                                Authorization: "Bearer " + currentToken,
                                Range: `bytes=${start}-${end}`,
                                'Cache-Control': 'no-cache, no-store, must-revalidate',
                                'Pragma': 'no-cache',
                            },
                            cache: 'no-store',
                        });
                    }
                }
                if (!response.ok) {
                    controller.error(new Error(`Failed to fetch chunk ${i}`));
                    return;
                }
                const encryptedChunk = new Uint8Array(await response.arrayBuffer());
                const decryptedChunk = await decryptData(symmetricKey, encryptedChunk);
                receivedLength += decryptedChunk.length;
                if (onProgress) {
                    onProgress({
                        fileId: progressData.fileId,
                        status: progressData.status,
                        loaded: receivedLength,
                        total: progressData.size,
                        estimated: calculateEstimated(startTime, progressData.loadedFilesBytes + receivedLength, progressData.totalFilesBytes),
                        loadedFiles: progressData.loadedFiles,
                        totalFiles: progressData.totalFiles,
                        loadedFilesBytes: progressData.loadedFilesBytes,
                        totalFilesBytes: progressData.totalFilesBytes,
                    });
                }
                controller.enqueue(decryptedChunk); // Stream the decrypted data
            }
            controller.close(); // Finish the stream
        },
        cancel(reason) {
            canceledByConsumer = true;
            downloadController.abort(reason);
        },
    });
    return readableStream;
};
export function encodeSendObjectToBase58(sendObject) {
    const sendString = JSON.stringify(sendObject);
    const sendBytesEncoded = encodeToBase58(sendString);
    return sendBytesEncoded;
}
export async function commRequest(method, url, authToken, getParams, formData, responseType, rangeHeader, onUploadProgress, onDownloadProgress, _isRetry, signal) {
    const logsAndVars = createEmptyLogsAndVars("dwcommonutils:sendMessageWithMpcWallet");
    try {
        //console.log("authToken (param)", authToken);
        //console.log("method (param)", method);
        //console.log("url (param)", url);
        //console.log("getParams (param)", getParams);
        //console.log("formData (param)", formData);
        //console.log("responseType (param)", responseType);
        recordVariable(logsAndVars, "authToken (param)", authToken);
        recordVariable(logsAndVars, "method (param)", method);
        recordVariable(logsAndVars, "url (param)", url);
        recordVariable(logsAndVars, "getParams (param)", getParams);
        recordVariable(logsAndVars, "formData (param)", formData);
        recordVariable(logsAndVars, "responseType (param)", responseType);
        // const url = baseUrl + endpoint;
        // recordVariable(logsAndVars, "url", url);
        const axiosConfig = {
            responseType: "json",
            signal,
        };
        axiosConfig.headers = {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
        };
        if (authToken !== undefined) {
            axiosConfig.headers.Authorization = `Bearer ${authToken}`;
        }
        if (rangeHeader !== undefined) {
            axiosConfig.headers.Range = rangeHeader;
        }
        if (getParams !== undefined) {
            axiosConfig.params = {
                ...getParams,
            };
        }
        if (responseType !== undefined) {
            axiosConfig.responseType = responseType;
        }
        if (onUploadProgress) {
            axiosConfig.onUploadProgress = onUploadProgress;
        }
        if (onDownloadProgress) {
            axiosConfig.onDownloadProgress = onDownloadProgress;
        }
        let responsePromise;
        if (method === "post") {
            responsePromise = axios.post(url, formData ?? "", axiosConfig);
        }
        else if (method === "put") {
            responsePromise = axios.put(url, formData, axiosConfig);
        }
        else if (method === "delete") {
            responsePromise = axios.delete(url, axiosConfig);
        }
        else if (method === "get") {
            responsePromise = axios.get(url, axiosConfig);
        }
        if (responsePromise === undefined) {
            throw new ReportingError("query cannot be started: " + url, logsAndVars);
        }
        //try {
        const awaitedResponse = await responsePromise;
        recordVariable(logsAndVars, "axios response", awaitedResponse);
        const res = awaitedResponse.data;
        //console.log("sendMessageWithMpcWallet", method, url, params, "result:", JSON.stringify(awaitedResponse));
        return res;
    }
    catch (error) {
        if (signal?.aborted ||
            error?.name === "CanceledError" ||
            error?.name === "AbortError" ||
            error?.code === "ERR_CANCELED") {
            throw error;
        }
        if (!_isRetry && authToken && error?.response?.status === 401) {
            const newToken = await refreshAuthToken();
            if (signal?.aborted) {
                throw signal.reason ?? Object.assign(new Error("canceled"), { name: "AbortError" });
            }
            if (newToken) {
                return commRequest(method, url, newToken, getParams, formData, responseType, rangeHeader, onUploadProgress, onDownloadProgress, true, signal);
            }
        }
        await recordPotentialAxiosError(logsAndVars, error);
        throw new ReportingError(error, logsAndVars);
    }
}
export function endPointToUrl(endpoint) {
    //return "https://httpbin.org" + endpoint;
    return baseUrl + endpoint;
}
